import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Mail, Lock, User, Building2, Phone, Loader2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import Logo from "@/components/site/Logo";

function Field({ id, label, icon: Icon, type = "text", value, onChange, placeholder, testid, required = true }) {
    return (
        <div>
            <Label htmlFor={id} className="text-slate-700">{label}</Label>
            <div className="relative mt-1.5">
                <Icon className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <Input id={id} type={type} required={required} data-testid={testid} value={value} onChange={onChange} className="pl-9 h-11" placeholder={placeholder} />
            </div>
        </div>
    );
}

export default function OpretKonto() {
    const navigate = useNavigate();
    const { register } = useAuth();
    const [form, setForm] = useState({
        name: "", company_name: "", email: "", phone: "", password: "", confirm: "",
    });
    const [error, setError] = useState("");
    const [loading, setLoading] = useState(false);

    const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

    const submit = async (e) => {
        e.preventDefault();
        setError("");
        if (form.password.length < 6) {
            setError("Adgangskoden skal være mindst 6 tegn.");
            return;
        }
        if (form.password !== form.confirm) {
            setError("Adgangskoderne er ikke ens.");
            return;
        }
        setLoading(true);
        try {
            await register({
                name: form.name,
                company_name: form.company_name,
                email: form.email,
                phone: form.phone,
                password: form.password,
            });
            navigate("/dashboard", { replace: true });
        } catch (err) {
            setError(formatApiError(err));
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-[calc(100vh-4rem)] flex items-center justify-center bg-gradient-to-br from-slate-50 via-blue-50/30 to-purple-50/30 px-4 py-12">
            <div className="w-full max-w-lg">
                <div className="flex justify-center mb-6"><Logo height={60} /></div>
                <div data-testid="register-card" className="bg-white rounded-2xl shadow-xl shadow-slate-900/5 border border-slate-200/70 p-7">
                    <h1 className="text-2xl font-bold text-slate-900 tracking-tight">Opret konto</h1>
                    <p className="text-sm text-slate-500 mt-1">Kom i gang med MyServiceHelper i dag.</p>

                    {/* Google Sign-in — REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH */}
                    <button
                        type="button"
                        data-testid="register-google-btn"
                        onClick={() => {
                            const redirectUrl = window.location.origin + "/dashboard";
                            window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
                        }}
                        className="mt-6 w-full h-11 rounded-xl border border-slate-300 bg-white hover:bg-slate-50 flex items-center justify-center gap-3 font-medium text-slate-700 shadow-sm transition-colors"
                    >
                        <svg width="18" height="18" viewBox="0 0 48 48" xmlns="http://www.w3.org/2000/svg">
                            <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3c-1.6 4.7-6.1 8-11.3 8-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34 6.1 29.3 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.3-.4-3.5z"/>
                            <path fill="#FF3D00" d="M6.3 14.1l6.6 4.8C14.7 15.1 19 12 24 12c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34 6.1 29.3 4 24 4 16.3 4 9.6 8.3 6.3 14.1z"/>
                            <path fill="#4CAF50" d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.2c-2 1.5-4.5 2.4-7.2 2.4-5.2 0-9.6-3.3-11.3-8l-6.5 5C9.5 39.6 16.2 44 24 44z"/>
                            <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.3-2.2 4.2-4.1 5.6l6.2 5.2c-.4.4 6.6-4.8 6.6-14.8 0-1.3-.1-2.3-.4-3.5z"/>
                        </svg>
                        <span>Fortsæt med Google</span>
                    </button>

                    <div className="my-5 flex items-center gap-3">
                        <div className="flex-1 h-px bg-slate-200" />
                        <span className="text-xs uppercase tracking-wider text-slate-400 font-medium">Eller opret med email</span>
                        <div className="flex-1 h-px bg-slate-200" />
                    </div>

                    <form onSubmit={submit} className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div className="sm:col-span-2">
                            <Field id="name" label="Navn" icon={User} value={form.name} onChange={update("name")} placeholder="Jens Hansen" testid="register-name-input" />
                        </div>
                        <div className="sm:col-span-2">
                            <Field id="company" label="Virksomhedsnavn" icon={Building2} value={form.company_name} onChange={update("company_name")} placeholder="Hansen Service ApS" testid="register-company-input" />
                        </div>
                        <div className="sm:col-span-2">
                            <Field id="email" label="Email" icon={Mail} type="email" value={form.email} onChange={update("email")} placeholder="navn@firma.dk" testid="register-email-input" />
                        </div>
                        <div className="sm:col-span-2">
                            <Field id="phone" label="Telefon" icon={Phone} type="tel" value={form.phone} onChange={update("phone")} placeholder="60 12 34 56" testid="register-phone-input" required={false} />
                        </div>
                        <Field id="password" label="Adgangskode" icon={Lock} type="password" value={form.password} onChange={update("password")} placeholder="Min. 6 tegn" testid="register-password-input" />
                        <Field id="confirm" label="Gentag adgangskode" icon={Lock} type="password" value={form.confirm} onChange={update("confirm")} placeholder="••••••••" testid="register-confirm-input" />

                        {error && (
                            <div data-testid="register-error" className="sm:col-span-2 text-sm text-red-600 bg-red-50 border border-red-100 rounded-lg px-3 py-2">{error}</div>
                        )}

                        <Button
                            type="submit"
                            disabled={loading}
                            data-testid="register-submit-btn"
                            className="sm:col-span-2 w-full h-11 rounded-xl bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white shadow-md"
                        >
                            {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                            Opret konto
                        </Button>
                    </form>

                    <p className="text-sm text-slate-500 text-center mt-6">
                        Har du allerede en konto?{" "}
                        <Link to="/login" data-testid="register-login-link" className="text-blue-600 hover:text-blue-700 font-medium">Log ind</Link>
                    </p>
                </div>
            </div>
        </div>
    );
}
